import {
  getPaymentMethodDetail,
  getPaymentMethodIcon,
  getPaymentMethodTitle
} from './paymentMethodDisplay'
import { Alert, Button, DropdownMenu, Prompt, AnimatedContentLoader } from '@components'
import { useBusinessContext } from '@context'
import {
  ActiveLabel,
  CardWrapper,
  ContentCol,
  HeaderRow,
  IconCol,
  StyledCard
} from '@root/containers/Stripe/components/SubscriptionRowCard'
import { BillingSetupModal } from '@root/containers/Billing/BillingSetupPanel'
import { deletePaymentMethod, setDefaultPaymentMethod } from '@root/query'
import {
  stripeBillingQueryKeys,
  useBillingDetailsPortal,
  usePaymentMethodSetup,
  usePaymentMethodStatus,
  usePaymentMethods
} from '@root/query/hooks'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaEllipsisV, FaExternalLinkAlt, FaPlus, FaStar, FaTrash } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

export const PaymentMethodSection: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const { data, isLoading } = usePaymentMethods({ slug })
  const { data: paymentMethodStatus } = usePaymentMethodStatus({ slug })
  const { startPaymentMethodSetup, isStartingPaymentMethodSetup } = usePaymentMethodSetup({
    slug
  })
  const { openBillingDetailsPortal, isOpeningBillingDetailsPortal } = useBillingDetailsPortal({
    slug
  })
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<string | null>(null)
  const [billingSetupModalOpen, setBillingSetupModalOpen] = useState(false)
  const isUsageBillingPaymentAtRisk = paymentMethodStatus?.usage_billing_payment_at_risk ?? false

  const paymentMethods = data?.payment_methods ?? []
  const isLastPaymentMethod = paymentMethods.length === 1

  const invalidatePaymentMethods = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: stripeBillingQueryKeys.all(slug) }),
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      queryClient.invalidateQueries({ queryKey: [slug] })
    ])
  }

  const handleBillingSetupSuccess = async () => {
    await invalidatePaymentMethods()
    setBillingSetupModalOpen(false)
  }

  const renderPaymentMethodActions = () => (
    <ButtonRow>
      <Button
        type="button"
        intent="primary"
        icon={<FaPlus />}
        onClick={() => setBillingSetupModalOpen(true)}
      >
        {t('settings.billing.addPaymentMethod')}
      </Button>
    </ButtonRow>
  )

  const { mutateAsync: setDefault, isPending: isSettingDefault } = useMutation({
    mutationFn: (paymentMethodId: string) =>
      setDefaultPaymentMethod({ slug, paymentMethodId }, undefined),
    onSuccess: invalidatePaymentMethods
  })

  const { mutateAsync: removePaymentMethod, isPending: isDeletingPaymentMethod } = useMutation({
    mutationFn: (paymentMethodId: string) =>
      deletePaymentMethod({ slug, paymentMethodId }, undefined),
    onSuccess: async () => {
      setPaymentMethodToDelete(null)
      await invalidatePaymentMethods()
      toast.success(t('settings.billing.paymentMethodRemovedSuccess'))
    }
  })

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <Section>
        {isUsageBillingPaymentAtRisk && (
          <Alert
            type="warning"
            title={t('settings.billing.usageBillingPaymentAtRisk.alertTitle')}
            description={
              <PaymentAtRiskContent>
                <p>{t('settings.billing.usageBillingPaymentAtRisk.alertDescription')}</p>
                {renderPaymentMethodActions()}
              </PaymentAtRiskContent>
            }
          />
        )}
        {paymentMethods.length === 0 && !isUsageBillingPaymentAtRisk && (
          <EmptyState>{t('settings.billing.paymentMethodsEmpty')}</EmptyState>
        )}
        {paymentMethods.length > 0 && (
          <CardWrapper>
            {paymentMethods.map(paymentMethod => {
              const PaymentMethodIcon = getPaymentMethodIcon(paymentMethod)
              const paymentMethodTitle = getPaymentMethodTitle(paymentMethod, t)
              const paymentMethodDetail = getPaymentMethodDetail(paymentMethod, t)
              const menuItems = [
                !paymentMethod.is_default ? (
                  <Button
                    key="set-default"
                    type="button"
                    isSecondary
                    icon={<FaStar />}
                    disabled={isSettingDefault}
                    onClick={() => void setDefault(paymentMethod.id)}
                  >
                    {t('settings.billing.setDefaultPaymentMethod')}
                  </Button>
                ) : null,
                <Button
                  key="delete"
                  type="button"
                  intent="danger"
                  icon={<FaTrash />}
                  onClick={() => setPaymentMethodToDelete(paymentMethod.id)}
                >
                  {t('settings.billing.deletePaymentMethod')}
                </Button>
              ].filter(Boolean) as React.ReactElement[]

              return (
                <StyledCard key={paymentMethod.id} $isActive={false}>
                  <IconCol $isActive={false}>
                    <PaymentMethodIcon aria-hidden />
                  </IconCol>
                  <ContentCol>
                    <HeaderRow>
                      <h4>{paymentMethodTitle}</h4>
                      <MenuWrapper>
                        {paymentMethod.is_default && (
                          <ActiveLabel>
                            {t('settings.billing.defaultPaymentMethodBadge')}
                          </ActiveLabel>
                        )}
                        <DropdownMenu items={menuItems}>
                          <Button
                            type="button"
                            isSecondary
                            icon={<FaEllipsisV />}
                            aria-label={t('settings.billing.paymentMethodActions')}
                          />
                        </DropdownMenu>
                      </MenuWrapper>
                    </HeaderRow>
                    {paymentMethodDetail ? <CardMeta>{paymentMethodDetail}</CardMeta> : null}
                  </ContentCol>
                </StyledCard>
              )
            })}
          </CardWrapper>
        )}

        {!isUsageBillingPaymentAtRisk && renderPaymentMethodActions()}

        <BillingDetailsSection>
          <BillingDetailsTitle>{t('settings.billing.billingDetailsTitle')}</BillingDetailsTitle>
          <BillingDetailsDescription>
            {t('settings.billing.billingDetailsPortalDescription')}
          </BillingDetailsDescription>
          <Button
            type="button"
            isSecondary
            icon={<FaExternalLinkAlt />}
            showSpinner={isOpeningBillingDetailsPortal}
            disabled={isOpeningBillingDetailsPortal}
            onClick={() => void openBillingDetailsPortal()}
          >
            {t('settings.billing.updateBillingDetailsOnStripe')}
          </Button>
        </BillingDetailsSection>

        <Prompt
          isVisible={Boolean(paymentMethodToDelete)}
          onClose={() => setPaymentMethodToDelete(null)}
          title={t('settings.billing.deletePaymentMethodTitle')}
          description={
            isLastPaymentMethod
              ? t('settings.billing.deleteLastPaymentMethodDescription')
              : t('settings.billing.deletePaymentMethodDescription')
          }
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setPaymentMethodToDelete(null)
            },
            {
              text: t('settings.billing.deletePaymentMethod'),
              intent: 'danger',
              showSpinner: isDeletingPaymentMethod,
              disabled: isDeletingPaymentMethod,
              action: () => {
                if (paymentMethodToDelete) {
                  void removePaymentMethod(paymentMethodToDelete)
                }
              }
            }
          ]}
        />

        <BillingSetupModal
          isVisible={billingSetupModalOpen}
          onClose={() => setBillingSetupModalOpen(false)}
          mode="payment-method"
          initialSelectedCardId="card"
          onStripeAction={() => void startPaymentMethodSetup({})}
          isStripeActionPending={isStartingPaymentMethodSetup}
          onManualSubmitSuccess={handleBillingSetupSuccess}
        />
      </Section>
    </AnimatedContentLoader>
  )
}

const PaymentAtRiskContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  p {
    margin: 0;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const EmptyState = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const CardMeta = styled.span`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const BillingDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const BillingDetailsTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const BillingDetailsDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`
